import type { PayloadAction } from '@reduxjs/toolkit';
import type { TSlot, TSlotFilling, TSlotOption } from '@hubertai/screening-question-types';
import type { QuestionEditorsState } from './Question.Editor.Slice';

// Slot-filling editor reducers (B2: optional structured-field extraction on Open questions).
// Mirrors question.intents.reducer — mutates state.byId[questionId].slot_filling and flags stateModified.
// Discriminator everywhere: a question "has slot filling" iff slot_filling.slots.length > 0, so removing
// the last slot clears slot_filling entirely (matches the chat-backend check).

// Start with an empty id: it is the extraction field name in the prompt AND the storage key on the
// saved answer, so it must be author-chosen and semantic (e.g. 'date_of_birth'). A random id is a
// weaker cue for the model and unreadable in stored answers/reports — and forces the author to clear
// it before typing.
const getEmptySlot = (): TSlot => ({
  id: '',
  label: {},
  datatype: 'text',
  required: true,
});

const getEmptySlotOption = (): TSlotOption => ({ value: '', label: {} });

export const questionSlotsReducer = {
  addSlot: (state: QuestionEditorsState, action: PayloadAction<{ questionId: string }>) => {
    const question = state.byId[action.payload.questionId];
    if (!question) return;
    if (!question.slot_filling) question.slot_filling = { slots: [] };
    question.slot_filling.slots.push(getEmptySlot());
    question.stateModified = true;
  },

  removeSlot: (state: QuestionEditorsState, action: PayloadAction<{ questionId: string; index: number }>) => {
    const { questionId, index } = action.payload;
    const question = state.byId[questionId];
    if (!question?.slot_filling) return;
    question.slot_filling.slots.splice(index, 1);
    if (question.slot_filling.slots.length === 0) question.slot_filling = undefined;
    question.stateModified = true;
  },

  updateSlot: (
    state: QuestionEditorsState,
    action: PayloadAction<{ questionId: string; index: number; patch: Partial<TSlot> }>,
  ) => {
    const { questionId, index, patch } = action.payload;
    const question = state.byId[questionId];
    if (!question?.slot_filling) return;
    const slot = question.slot_filling.slots[index];
    if (!slot) return;
    Object.assign(slot, patch);
    question.stateModified = true;
  },

  addSlotOption: (state: QuestionEditorsState, action: PayloadAction<{ questionId: string; slotIndex: number }>) => {
    const { questionId, slotIndex } = action.payload;
    const question = state.byId[questionId];
    if (!question?.slot_filling) return;
    const slot = question.slot_filling.slots[slotIndex];
    if (!slot) return;
    if (!slot.options) slot.options = [];
    slot.options.push(getEmptySlotOption());
    question.stateModified = true;
  },

  updateSlotOption: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      slotIndex: number;
      optionIndex: number;
      patch: Partial<TSlotOption>;
    }>,
  ) => {
    const { questionId, slotIndex, optionIndex, patch } = action.payload;
    const question = state.byId[questionId];
    if (!question?.slot_filling) return;
    const option = question.slot_filling.slots[slotIndex]?.options?.[optionIndex];
    if (!option) return;
    Object.assign(option, patch);
    question.stateModified = true;
  },

  removeSlotOption: (
    state: QuestionEditorsState,
    action: PayloadAction<{ questionId: string; slotIndex: number; optionIndex: number }>,
  ) => {
    const { questionId, slotIndex, optionIndex } = action.payload;
    const question = state.byId[questionId];
    if (!question?.slot_filling) return;
    const slot = question.slot_filling.slots[slotIndex];
    if (!slot?.options) return;
    slot.options.splice(optionIndex, 1);
    question.stateModified = true;
  },

  updateSlotFilling: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      patch: Partial<Pick<TSlotFilling, 'extraction_guidance' | 'max_reasks' | 'confirm'>>;
    }>,
  ) => {
    const { questionId, patch } = action.payload;
    const question = state.byId[questionId];
    if (!question) return;
    if (!question.slot_filling) question.slot_filling = { slots: [] };
    Object.assign(question.slot_filling, patch);
    question.stateModified = true;
  },
};
